"use client";
// ===================================================
// DevStatusBoard — "מצב פיתוח" (Dev Status, Phase 1)
// ===================================================
// ONE shared component, TWO mounts (Gal's decision, 10.06.2026):
//   canonical home: /dashboard/system-monitoring (the checks surface)
//   related window: /dashboard/vdev (a section — maintained HERE, shown there)
// Read-only. Data: dev_status_items + dev_status_drift (filled by
// scripts/dev-status-push.ts locally and the sync-dev-status EF in the cloud).
// Spec: docs/specs/dev-status-phase1-spec.md Task 5.

import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { GitCockpit } from "./GitCockpit";
import { CustodyControlPlane } from "./CustodyControlPlane";
import { DRAFT_TAGS, reduceOpenDrafts, type DraftEventRow, type OpenDraft } from "@/lib/drafts/open-drafts";
import { summarizeBlocks, type BlockRow, type BlockSummary } from "@/lib/vblocks/block-summary";

type Item = {
  surface: string; item_kind: string; external_id: string; name: string;
  status: string | null; is_dirty: boolean | null;
  ahead: number | null; behind: number | null;
  metadata: Record<string, unknown> | null; deep_link: string | null;
  shared_with?: string | null; purpose?: string | null;
  // LAND | REVIEW | DECIDE | RECLAIM — set by dev-status-push from
  // work-state-scan --triage, constrained by a CHECK in the DB.
  triage_class?: string | null;
  snapshot_at: string;
};
type Drift = { severity: string; message: string; surface: string; item: string };
type Stranded = { path: string; branch: string; approved_marker: string; last_seen_at: string };

function ago(iso?: string): string {
  if (!iso) return "—";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return `לפני ${m} דק'`;
  if (m < 48 * 60) return `לפני ${Math.round(m / 60)} שע'`;
  return `לפני ${Math.round(m / 1440)} ימים`;
}
const short = (sha?: unknown) => (typeof sha === "string" ? sha.slice(0, 7) : "—");

export function DevStatusBoard() {
  const [items, setItems] = useState<Item[]>([]);
  const [drift, setDrift] = useState<Drift[]>([]);
  const [stranded, setStranded] = useState<Stranded[]>([]);
  // 📝 טיוטות פתוחות (DEC-0395). Reduced client-side from session_events —
  // see src/lib/drafts/open-drafts.ts for why. draftsError is the loud-failure
  // path: a failed query renders ONE error line, it never vanishes silently.
  const [drafts, setDrafts] = useState<OpenDraft[]>([]);
  const [draftsError, setDraftsError] = useState<string | null>(null);
  const [draftsLastEvent, setDraftsLastEvent] = useState<string | undefined>(undefined);
  // 🧱 vBlocks — the existing vb_blocks table (old rendering mechanism), same
  // shape as the drafts section: reduced client-side, loud failure line.
  const [blocks, setBlocks] = useState<BlockSummary | null>(null);
  const [blocksError, setBlocksError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [i, d, g, se, vb] = await Promise.all([
        supabase.from("dev_status_items").select("*"),
        supabase.from("dev_status_drift").select("*"),
        // הדופק (Rule 35 §7) — approved-yet-unlanded governance docs. Degrades to
        // "zero" if the table isn't there yet (supabase-js returns {data:null}, no throw).
        supabase.from("governance_landing_status")
          .select("path,branch,approved_marker,last_seen_at")
          .eq("status", "stranded"),
        // Drafts: every bus line carrying a draft-* tag. Measured 06.09.2026:
        // `authenticated` holds SELECT on session_events (policy se_read), and
        // /dashboard/* sits behind the Supabase cookie session — same read path
        // as dev_status_items above, no RLS change and no server route needed.
        supabase.from("session_events")
          .select("occurred_at,tags,payload")
          .overlaps("tags", [...DRAFT_TAGS])
          .order("occurred_at", { ascending: false })
          .limit(1000),
        // vBlocks: RLS policy ws_blocks (SELECT, role public) scopes rows to the
        // caller's workspaces via vb_user_workspace_ids(). Measured 06.09.2026:
        // all 57 blocks hang off records in «G.A.M Services», and both of Gal's
        // logins are active members there — so the browser read returns them
        // all, with no RLS change and no server route. A user outside that
        // workspace legitimately sees zero and the section stays hidden.
        supabase.from("vb_blocks")
          .select("type,record_id,updated_at")
          .limit(5000),
      ]);
      setItems((i.data as Item[]) ?? []);
      setDrift((d.data as Drift[]) ?? []);
      setStranded((g.data as Stranded[]) ?? []);
      if (se.error) {
        setDraftsError(se.error.message);
      } else {
        const rows = (se.data as DraftEventRow[]) ?? [];
        setDrafts(reduceOpenDrafts(rows));
        setDraftsLastEvent(rows[0]?.occurred_at);
      }
      if (vb.error) {
        setBlocksError(vb.error.message);
      } else {
        setBlocks(summarizeBlocks((vb.data as BlockRow[]) ?? []));
      }
      setLoading(false);
    })();
  }, []);

  const by = (s: string) => items.filter((x) => x.surface === s);
  const fresh = (s: string) =>
    ago(by(s).map((x) => x.snapshot_at).sort().at(-1));

  const local = by("local_git");
  const gh = by("github");
  const vc = by("vercel");
  const sb = by("supabase");
  const pv = by("preview").filter((x) => x.status === "active");

  const current = local.find((x) => x.item_kind === "branch" && x.status === "current");
  const tree = local.find((x) => x.item_kind === "summary");
  const unpushed = local.filter((x) => x.item_kind === "branch" && (x.ahead ?? 0) > 0);
  const ghMain = gh.find((x) => x.item_kind === "branch" && x.external_id === "main");
  const prs = gh.filter((x) => x.item_kind === "pr");
  const alerts = gh.find((x) => x.item_kind === "alert");
  const prod = vc.find((x) => x.item_kind === "summary" && x.external_id === "production");
  const deploys = vc.filter((x) => x.item_kind === "deployment").slice(0, 5);
  const noSource = sb.filter((x) => x.status === "no_source");
  // CI (DEC-0251 round 1). surface='ci' rows are written by /opt/ci-runner.sh on
  // VPS#1 through the report_ci_run RPC, fired by the bare repo's post-receive on
  // every push to main. This board filters by surface in code, so a new surface
  // shows up nowhere until it is read here — which is the Rule 26 gap this closes.
  // 'ci' carries two different kinds of thing, and mixing them would make the
  // headline lie: item_kind='check' is a verdict about a commit on main, while
  // 'checkout' is a fact about a working copy on Gal's Mac (round 4). A stale
  // checkout is a real problem, but it is not "the tests are failing".
  const ci = by("ci").filter((x) => x.item_kind !== "checkout");
  const ciFailed = ci.filter((x) => x.status === "failed");
  const ciUnmeasured = ci.filter((x) => x.status === "not_measured");
  const checkout = by("ci").find((x) => x.item_kind === "checkout");
  // active sessions = worktree rows that declared intent via .dev-session.json
  // (Rule 31 — "which session is on which worktree, doing what").
  const sessions = local
    .filter((x) => x.item_kind === "worktree" && x.purpose)
    .sort((a, b) => (b.snapshot_at ?? "").localeCompare(a.snapshot_at ?? ""));

  // ⏱️ פעימות עבודה — surface='chip_progress', נכתב על ידי chip_beat().
  // Until 28.08 this surface was written and never read: grep for
  // "chip_progress" across src/ returned ZERO hits, so a supervisor who wanted
  // to know whether live work was still moving had to run SQL by hand. Same
  // Rule 26 gap, same fix, as the 'ci' surface a few lines above.
  // The reason this matters now (spec §6.12, measured): hooks that report a
  // session's work fire on Stop, and a long turn has no Stop until it ends —
  // 362 of 1,286 tool calls in one lane session, 28.1%, never reached the bus
  // because compaction ended the stretch instead of a hand-back. A beat is the
  // only signal that crosses that gap, and it is worthless while invisible.
  // Thresholds mirror v_chip_progress_live exactly (5 min / 30 min) so the page
  // and the view can never disagree about what "live" means.
  const beats = by("chip_progress")
    .sort((a, b) => (b.snapshot_at ?? "").localeCompare(a.snapshot_at ?? ""));
  const beatHealth = (iso: string) => {
    const min = (Date.now() - new Date(iso).getTime()) / 60000;
    if (min < 5) return { he: "חי", tone: "text-teal-300" };
    if (min < 30) return { he: "מאט", tone: "text-amber-300" };
    return { he: "שקט", tone: "text-rose-300" };
  };

  // Branch triage (Rule 35 §3 made operational). A proposal, never an action —
  // nothing on this board merges, parks or deletes anything.
  const TRIAGE = [
    { k: "LAND", he: "נוחת", why: "נקי וקטן — הזול ביותר לסגירה", tone: "text-teal-300" },
    { k: "REVIEW", he: "לבדיקה", why: "נקי אבל גדול — עדיין רוצים?", tone: "text-slate-300" },
    { k: "DECIDE", he: "להכריע", why: "מתנגש, עד 30 יום", tone: "text-amber-300" },
    { k: "RECLAIM", he: "לחלץ", why: "מתנגש, מעל חודש", tone: "text-rose-300" },
  ] as const;
  const triaged = local.filter((x) => x.item_kind === "branch" && x.triage_class);
  // A branch that is someone's live workspace is excluded from the LAND queue:
  // on 2026-08-03 a branch that read as clean and merged was WHITE-S06's
  // workspace, and acting on it would have pulled the directory out from under
  // a running conversation (Rule 31).
  const heldPaths = new Set(
    local.filter((x) => x.item_kind === "worktree").map((x) => x.status ?? ""),
  );
  const landQueue = triaged
    .filter((x) => x.triage_class === "LAND" && !heldPaths.has(x.name))
    .slice(0, 8);

  if (loading) {
    return <div className="text-sm text-slate-400 p-4">טוען מצב פיתוח…</div>;
  }

  const col = "bg-slate-800/30 rounded-xl border border-slate-700 p-4 space-y-2";
  const head = "flex items-center justify-between text-sm font-semibold text-slate-200";
  const stamp = "text-[11px] text-slate-500";
  const line = "text-xs text-slate-300 flex justify-between gap-2";

  return (
    <div dir="rtl" className="space-y-3">
      <CustodyControlPlane />
      {/* live, actionable pipeline state for the current branch (reuses git-actions, Rule 10) */}
      <GitCockpit />
      {/* drift bar — the whole point of the board */}
      {drift.length > 0 && (
        <div className="space-y-1">
          {drift.map((d, i) => (
            <div key={i}
              className={`text-xs rounded-lg px-3 py-1.5 border ${
                d.severity === "red"
                  ? "bg-red-950/40 border-red-800 text-red-300"
                  : "bg-yellow-950/30 border-yellow-800/60 text-yellow-300"
              }`}>
              {d.severity === "red" ? "🔴" : "🟡"} {d.message}
            </div>
          ))}
        </div>
      )}
      {drift.length === 0 && (
        <div className="text-xs rounded-lg px-3 py-1.5 border bg-emerald-950/30 border-emerald-800/60 text-emerald-300">
          🟢 אפס drift — מחשב, GitHub ו-Vercel מסונכרנים
        </div>
      )}

      {/* הדופק (Rule 35 §7) — מסמכי ממשל מאושרים שלא נחתו על main = עבודה תקועה */}
      {stranded.length > 0 ? (
        <div className="space-y-2">
          <div className="text-xs rounded-lg px-3 py-1.5 border bg-red-950/40 border-red-800 text-red-300">
            🔴 {stranded.length} מסמכים מאושרים תקועים על ענף שלא מוזג — מאושר שלא נחת = לא קיים (Rule 35)
          </div>
          <div className={col}>
            <div className={head}><span>📌 מאושר־ותקוע ({stranded.length})</span><span className={stamp}>{fresh("local_git")}</span></div>
            {stranded.map((s) => (
              <div key={`${s.path}@${s.branch}`} className={line}>
                <span className="truncate text-slate-300">{s.path}</span>
                <span className="text-slate-500 text-[11px] shrink-0">{s.approved_marker} · {s.branch}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-xs rounded-lg px-3 py-1.5 border bg-emerald-950/30 border-emerald-800/60 text-emerald-300">
          🟢 אפס מאושר־ותקוע — כל הידע המאושר נחת ב-main (Rule 35 · הדופק)
        </div>
      )}

      {/* 🧪 CI — בדיקות יחידה על כל push ל-main (DEC-0251 סבב 1).
          אין שורות = "טרם דווח", לעולם לא ירוק. שורה ירוקה שמשמעותה "לא רץ"
          היא בדיוק הכשל שהסבב הזה נועד לסגור. */}
      {ci.length === 0 ? (
        <div className="text-xs rounded-lg px-3 py-1.5 border bg-slate-800/40 border-slate-700 text-slate-400">
          ⚪ CI — טרם דווח. הריצה הראשונה תופיע אחרי ה-push הבא ל-main
        </div>
      ) : (
        <div className="space-y-2">
          <div
            className={`text-xs rounded-lg px-3 py-1.5 border ${
              ciFailed.length > 0
                ? "bg-red-950/40 border-red-800 text-red-300"
                : ciUnmeasured.length > 0
                  ? "bg-yellow-950/30 border-yellow-800/60 text-yellow-300"
                  : "bg-emerald-950/30 border-emerald-800/60 text-emerald-300"
            }`}>
            {ciFailed.length > 0
              ? ciFailed.length === 1
                ? "🔴 בדיקות היחידה נכשלות על main"
                : `🔴 ${ciFailed.length} בדיקות נכשלות על main`
              : ciUnmeasured.length > 0
                ? ciUnmeasured.length === 1
                  ? "🟡 בדיקות היחידה לא נמדדו — זו אינה הצלחה"
                  : `🟡 ${ciUnmeasured.length} בדיקות לא נמדדו — זו אינה הצלחה`
                : "🟢 בדיקות היחידה עוברות על main"}
          </div>
          <div className={col}>
            <div className={head}><span>🧪 CI — בדיקות על main</span><span className={stamp}>{fresh("ci")}</span></div>
            {ci.map((c) => {
              const md = (c.metadata ?? {}) as Record<string, unknown>;
              const tone =
                c.status === "failed" ? "text-rose-300"
                : c.status === "not_measured" ? "text-amber-300"
                : "text-teal-300";
              const label =
                c.status === "failed" ? "נכשל"
                : c.status === "not_measured" ? "לא נמדד"
                : "עבר";
              return (
                <div key={c.external_id} className={line}>
                  <span className="truncate">{c.name}</span>
                  <span className="shrink-0 flex gap-2">
                    <span className="text-slate-500 text-[11px]">{short(md.sha)}</span>
                    <b className={tone}>{label}</b>
                  </span>
                </div>
              );
            })}
            <div className="text-[11px] text-slate-500 pt-1 border-t border-slate-700/60">
              רץ בחדר נקי על VPS#1 בכל push ל-main · tsc ו-eslint נאכפים כבר ב-pre-push
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* 💻 local machine */}
        <div className={col}>
          <div className={head}><span>💻 המחשב</span><span className={stamp}>{fresh("local_git")}</span></div>
          <div className={line}><span>ענף נוכחי</span><b className="text-teal-400">{current?.name ?? "—"}</b></div>
          <div className={line}><span>עץ עבודה</span>
            <span>{tree?.is_dirty ? `${(tree.metadata as any)?.dirty_count} קבצים לא שמורים` : "נקי"}</span></div>
          <div className={line}><span>ענפים / worktrees / stashes</span>
            <span>{local.filter((x) => x.item_kind === "branch").length} / {local.filter((x) => x.item_kind === "worktree").length} / {local.filter((x) => x.item_kind === "stash").length}</span></div>
          {unpushed.length > 0 && (
            <div className="pt-1 border-t border-slate-700/60 space-y-1">
              <div className="text-[11px] text-yellow-400">קומיטים שקיימים רק כאן:</div>
              {unpushed.map((b) => (
                <div key={b.external_id} className={line}><span className="truncate">{b.name}</span><span>+{b.ahead}</span></div>
              ))}
            </div>
          )}
        </div>

        {/* 🐙 GitHub */}
        <div className={col}>
          <div className={head}><span>🐙 GitHub</span><span className={stamp}>{fresh("github")}</span></div>
          {gh.length === 0 ? (
            <div className="text-xs text-slate-500">ממתין לטוקן קריאה (צעד ידני GAL-1 ב-spec)</div>
          ) : (
            <>
              <div className={line}><span>main</span><b className="text-teal-400">{short(ghMain?.metadata?.sha)}</b></div>
              <div className={line}><span>ענפים בענן</span><span>{gh.filter((x) => x.item_kind === "branch").length}</span></div>
              <div className={line}><span>PRs פתוחים</span><span>{prs.length}</span></div>
              <div className={line}><span>התראות Dependabot</span><span>{alerts?.status ?? "—"}</span></div>
            </>
          )}
        </div>

        {/* ▲ Vercel */}
        <div className={col}>
          <div className={head}><span>▲ Vercel</span><span className={stamp}>{fresh("vercel")}</span></div>
          {vc.length === 0 ? (
            <div className="text-xs text-slate-500">ממתין לטוקן קריאה (צעד ידני GAL-2 ב-spec)</div>
          ) : (
            <>
              <div className={line}><span>production</span>
                <b className="text-teal-400">{short(prod?.metadata?.sha)} · {prod?.status ?? "—"}</b></div>
              {deploys.map((d) => (
                <div key={d.external_id} className={line}>
                  <span className="truncate">{d.name}</span>
                  <span className={d.status === "READY" ? "text-emerald-400" : d.status === "ERROR" ? "text-red-400" : "text-yellow-400"}>{d.status}</span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>


      {/* 🗂 The shared checkout (Rule 31) — its own element, NOT nested in the CI card,
          so it still renders on a board that has no check rows yet. Measured
          2026-08-17 at 1048 commits behind on a parked branch with nothing anywhere
          saying so, which is how a session came to read stale workflow files and
          report them as current. Report-only: nothing here pulls or switches it. */}
      {checkout && (
        <div className={col}>
          <div className={head}>
            <span>🗂 ה-checkout המשותף</span>
            <span className={stamp}>{ago(checkout.snapshot_at)}</span>
          </div>
          <div className={line}>
            <span className="truncate">
              {String((checkout.metadata as Record<string, unknown>)?.branch ?? "—")}
            </span>
            <span className="shrink-0 flex gap-2">
              <span className="text-slate-500 text-[11px]">
                {short((checkout.metadata as Record<string, unknown>)?.sha)}
              </span>
              <b className={
                checkout.status === "failed" ? "text-rose-300"
                : checkout.status === "not_measured" ? "text-amber-300"
                : "text-teal-300"
              }>
                {checkout.status === "failed" ? "לא עדכני"
                  : checkout.status === "not_measured" ? "לא נמדד" : "עדכני"}
              </b>
            </span>
          </div>
          {checkout.status !== "passed" && (
            <div className="text-[11px] text-slate-400">
              {(checkout.behind ?? 0) > 0 && `${checkout.behind} קומיטים מאחור · `}
              כל שיחה שקוראת קבצים משם מקבלת תמונה ישנה
            </div>
          )}
          <div className="text-[11px] text-slate-500 pt-1 border-t border-slate-700/60">
            דיווח בלבד — הלוח הזה לא מושך ולא מחליף ענף ב-checkout המשותף
          </div>
        </div>
      )}

      {/* 🧑‍💻 active sessions — worktrees that declared intent (Rule 31) */}
      {sessions.length > 0 && (
        <div className={col}>
          <div className={head}><span>🧑‍💻 שיחות פעילות ({sessions.length})</span><span className={stamp}>{fresh("local_git")}</span></div>
          {sessions.map((s) => (
            <div key={s.external_id} className="space-y-0.5 pb-1 border-b border-slate-700/40 last:border-0">
              <div className={line}>
                <span className="font-medium text-teal-400 truncate">{s.status}</span>
                <span className={stamp}>{ago(s.snapshot_at)}</span>
              </div>
              <div className="text-[11px] text-slate-400 truncate">
                {s.purpose}
                {s.shared_with && <span className="text-slate-500"> · ↗ {s.shared_with}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 🌿 branch triage — Rule 35 §3 made operational. Renders only when a
          scan has classified something, so it stays absent rather than showing
          a confident empty state on a machine that has never run --triage. */}
      {triaged.length > 0 && (
        <div className={col}>
          <div className={head}>
            <span>🌿 מיון ענפים ({triaged.length})</span>
            <span className={stamp}>{fresh("local_git")}</span>
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1">
            {TRIAGE.map((t) => {
              const rows = triaged.filter((x) => x.triage_class === t.k);
              if (!rows.length) return null;
              const free = rows.filter((x) => !heldPaths.has(x.name)).length;
              return (
                <div key={t.k} className={line} title={t.why}>
                  <span className={t.tone}>{t.he}</span>
                  {/* both numbers: showing only the total would overstate what
                      is actionable, since held branches are excluded below */}
                  <span className="tabular-nums text-slate-400">
                    {free}<span className="text-slate-600"> / {rows.length}</span>
                  </span>
                </div>
              );
            })}
          </div>
          {landQueue.length > 0 && (
            <div className="pt-2 mt-1 border-t border-slate-700/40 space-y-0.5">
              <div className="text-[11px] text-slate-500">הזולים לסגירה, בסדר עולה:</div>
              {landQueue.map((b) => (
                <div key={b.external_id} className="text-[11px] text-slate-400 truncate">
                  {b.name}
                </div>
              ))}
            </div>
          )}
          <div className="text-[11px] text-slate-500 pt-1">
            הצעה בלבד — הלוח הזה לא ממזג, לא מפרק ולא מוחק דבר.
          </div>
        </div>
      )}

      {/* ⏱️ work heartbeats — rendered only when surface='chip_progress' rows exist */}
      {beats.length > 0 && (
        <div className={col}>
          <div className={head}><span>⏱️ פעימות עבודה</span><span className={stamp}>{fresh("chip_progress")}</span></div>
          {beats.map((b) => {
            const h = beatHealth(b.snapshot_at);
            const meta = (b.metadata ?? {}) as Record<string, unknown>;
            const supervisor = typeof meta.supervisor === "string" ? meta.supervisor : null;
            const steps = Array.isArray(meta.steps) ? meta.steps.length : 0;
            return (
              <div key={b.external_id} className="space-y-1">
                <div className={line}>
                  <span className="truncate font-medium text-slate-200">{b.name}</span>
                  <span className={`${h.tone} whitespace-nowrap text-[11px]`}>{h.he} · {ago(b.snapshot_at)}</span>
                </div>
                {b.status && (
                  <div className="text-[11px] text-slate-400 truncate">{b.status}</div>
                )}
                <div className={`${line} text-slate-500 text-[11px]`}>
                  <span>{supervisor ? `מפקח ${supervisor}` : "בלי מפקח רשום"}</span>
                  <span>{steps} פעימות</span>
                </div>
              </div>
            );
          })}
          <div className="text-[11px] text-slate-500 pt-1">
            פעימה אינה הוכחת התקדמות — היא הוכחה שהעבודה עדיין זזה.
          </div>
        </div>
      )}

      {/* 📝 open drafts (DEC-0395) — rendered only when an open draft exists,
          or when the query itself failed (loud failure, never a silent blank) */}
      {(drafts.length > 0 || draftsError) && (
        <div className={col}>
          <div className={head}>
            <span>📝 טיוטות פתוחות{drafts.length > 0 ? ` — ${drafts.length}` : ""}</span>
            <span className={stamp}>אירוע אחרון {ago(draftsLastEvent)}</span>
          </div>
          {draftsError && (
            <div className="text-[11px] text-red-400">
              שאילתת הטיוטות נכשלה: {draftsError}
            </div>
          )}
          {drafts.map((d) => (
            <div key={d.from_subject} className="space-y-1">
              <div className={line}>
                <span className="truncate font-medium text-slate-200">{d.headline ?? "(בלי נושא)"}</span>
                <span className={`${d.state === "ישנה" ? "text-amber-300" : "text-teal-300"} whitespace-nowrap text-[11px]`}>
                  {d.state} · גיל {d.age_days} ימים
                </span>
              </div>
              <div className={`${line} text-slate-500 text-[11px]`}>
                <span>נפתחה {new Date(d.opened_at).toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit", timeZone: "Asia/Jerusalem" })}</span>
                <span className="truncate" dir="ltr">{d.from_subject}</span>
              </div>
            </div>
          ))}
          <div className="text-[11px] text-slate-500 pt-1">
            «ישנה» אינה מחיקה — טיוטה יוצאת רק דרך /draft-merge או /draft-archive.
          </div>
        </div>
      )}

      {/* 🧱 vBlocks — the existing vb_blocks table. Rendered only when the caller
          can see at least one block, or when the query itself failed (loud failure).
          NOT the DEC-0360 synced-content contract — that one has no table yet. */}
      {((blocks && blocks.total > 0) || blocksError) && (
        <div className={col}>
          <div className={head}>
            <span>🧱 vBlocks{blocks && blocks.total > 0 ? ` — ${blocks.total}` : ""}</span>
            <span className={stamp}>עדכון אחרון {ago(blocks?.last_updated)}</span>
          </div>
          {/* role classes / tokens only — the palette ratchet (Rule 24) forbids
              adding raw color classes to this file; it already carries 77. */}
          {blocksError && (
            <div className="text-[11px] text-error">
              שאילתת vb_blocks נכשלה: {blocksError}
            </div>
          )}
          {blocks && blocks.total > 0 && (
            <>
              <div className={`${stamp} flex justify-between gap-2`}>
                <span>{blocks.records} רשומות עם בלוקים</span>
                <span>{blocks.by_type.length} סוגים</span>
              </div>
              {blocks.by_type.map((t) => (
                <div key={t.type} className={line}>
                  <span className="truncate font-medium" dir="ltr">{t.type}</span>
                  <span className="whitespace-nowrap text-[11px]" style={{ color: "var(--cc-accent-400)" }}>
                    {t.blocks} בלוקים · {t.records} רשומות
                  </span>
                </div>
              ))}
              <div className={`${stamp} pt-1`}>
                מנגנון התצוגה הקיים (vb_blocks) — לא חוזה התוכן המסונכרן של DEC-0360.
              </div>
            </>
          )}
        </div>
      )}

      {/* 🔗 active preview sessions — rendered only when surface='preview' rows exist */}
      {pv.length > 0 && (
        <div className={col}>
          <div className={head}><span>🔗 פריביוז פעיל</span><span className={stamp}>{fresh("preview")}</span></div>
          {pv.map((p) => (
            <div key={p.external_id} className="space-y-1">
              <div className={line}>
                <span className="truncate font-medium text-teal-400">{p.name}</span>
                <a href={p.external_id} target="_blank" rel="noreferrer"
                  className="text-teal-400 underline truncate max-w-[160px] text-[11px]">{p.external_id}</a>
              </div>
              {(p.shared_with || p.purpose) && (
                <div className={`${line} text-slate-400 text-[11px]`}>
                  {p.shared_with && <span>↗ {p.shared_with}</span>}
                  {p.purpose && <span className="truncate">{p.purpose}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* supabase functions footer */}
      <div className="text-[11px] text-slate-500">
        ⚡ פונקציות ענן: {sb.length || "—"} פרוסות{noSource.length > 0 && (
          <span className="text-red-400"> · {noSource.length} בלי מקור בגיט</span>
        )} · עודכן {fresh("supabase")}
      </div>
    </div>
  );
}
